import type { ConsultationIntent } from '../agents/consultation-intent-router.js'
import {
  SESSION_PROJECTION_SCHEMA_VERSION,
  type SUPPORTED_UI_LOCALES,
} from '../contracts.js'
import type { FunnelStage } from '../types/funnel-stage.js'
import {
  createEmptyCanonicalConversationFlow,
  createInitialCanonicalAgentState,
  mergeCanonicalAgentState,
  normalizeCanonicalAgentSummary,
  normalizeCanonicalConversationFlow,
  type CanonicalAgentStateV1,
  type CanonicalAgentSummaryV1,
  type CanonicalConversationFlowV1,
} from './session-state.js'

export type CanonicalUiLocaleV1 = (typeof SUPPORTED_UI_LOCALES)[number]

export type CanonicalModalityV1 =
  | 'text'
  | 'voice'
  | 'screen'
  | 'webcam'
  | 'file'
  | 'tool'

export type CanonicalEventSourceV1 =
  | 'api'
  | 'browser'
  | 'live'
  | 'orchestrator'
  | 'agent'
  | 'tool'
  | 'report'
  | 'system'
  | 'migration'

export type CanonicalResponseOwnerV1 = 'live' | 'orchestrator' | 'api'

export type CanonicalCapabilityV1 =
  | 'grounded_research'
  | 'text_chat'
  | 'voice'
  | 'webcam'
  | 'screen_share'
  | 'file_analysis'
  | 'location_weather'
  | 'pdf_generation'
  | 'tool_call'

export interface CanonicalSourceV1 {
  title: string | null
  uri: string
  sourceType: string | null
}

export interface CanonicalSessionPermissionsV1 {
  research: boolean
  location: boolean
  voice: boolean
  webcam: boolean
  screen: boolean
  files: boolean
}

export interface CanonicalSessionAccessV1 {
  gated: boolean
  hasCompletedConcierge: boolean
  consentPersisted: boolean
  permissions: CanonicalSessionPermissionsV1
}

export interface CanonicalVisitorV1 {
  name: string | null
  email: string | null
  role: string | null
  identityConfidence: number
  identitySources: CanonicalSourceV1[]
  confirmedByUser: boolean
}

export interface CanonicalCompanyV1 {
  name: string | null
  domain: string | null
  website: string | null
  industry: string | null
  size: string | null
  summary: string | null
  confidence: number | null
  sources: CanonicalSourceV1[]
}

export interface CanonicalLocationV1 {
  granted: boolean
  latitude: number | null
  longitude: number | null
  city: string | null
  country: string | null
  updatedAt: string | null
}

export interface CanonicalModalityStatusV1 {
  active: boolean
  lastEventAt: string | null
}

export interface CanonicalModalitiesV1 {
  text: CanonicalModalityStatusV1
  voice: CanonicalModalityStatusV1
  screen: CanonicalModalityStatusV1
  webcam: CanonicalModalityStatusV1
  file: CanonicalModalityStatusV1
  tool: CanonicalModalityStatusV1
}

export type CanonicalTranscriptRoleV1 = 'user' | 'assistant' | 'system' | 'tool'
export type CanonicalTranscriptStateV1 = 'interim' | 'final' | 'corrected'

export interface CanonicalTranscriptEntryV1 {
  entryId: string
  turnId: string
  sequence: number
  revision: number
  role: CanonicalTranscriptRoleV1
  modality: CanonicalModalityV1
  state: CanonicalTranscriptStateV1
  source: CanonicalEventSourceV1
  occurredAt: string
  content: string
  evidenceIds: string[]
}

export type CanonicalEvidenceStatusV1 = 'unverified' | 'verified' | 'stale' | 'rejected'

export interface CanonicalEvidenceV1 {
  evidenceId: string
  turnId: string
  kind: CanonicalModalityV1 | 'research' | 'report'
  source: CanonicalEventSourceV1
  status: CanonicalEvidenceStatusV1
  summary: string
  storageRef: string | null
  confidence: number | null
  capturedAt: string
  verifiedAt: string | null
}

export interface CanonicalSessionFactV1 {
  key: string
  value: string
  source: 'intake' | 'user_text' | 'user_voice' | 'agent' | 'migration'
  confidence: number
  updatedAt: string
  evidenceIds: string[]
}

export interface CanonicalFactCorrectionV1 {
  correctionId: string
  key: string
  previousValue: string | null
  correctedValue: string
  turnId: string
  occurredAt: string
}

export interface CanonicalTurnCapabilityManifestV1 {
  turnId: string
  capabilities: CanonicalCapabilityV1[]
  successfulToolNames: string[]
  failedToolNames: string[]
  finalizedAt: string
}

export interface CanonicalActiveTurnV1 {
  turnId: string
  source: CanonicalEventSourceV1
  modality: CanonicalModalityV1
  startedAt: string
}

export interface CanonicalResponseOwnershipV1 {
  turnId: string
  owner: CanonicalResponseOwnerV1
  leaseId: string
  claimedAt: string
  leaseExpiresAt: string
  visibleOutputStartedAt: string | null
}

export interface CanonicalAgentDecisionV1 {
  decisionId: string
  turnId: string
  agent: string
  stage: FunnelStage
  createdAt: string
  metadata: Record<string, unknown>
}

export interface CanonicalBookingStateV1 {
  status: 'none' | 'requested' | 'booked' | 'cancelled'
  bookingId: string | null
  updatedAt: string | null
}

export interface CanonicalReportStateV1 {
  status: 'none' | 'requested' | 'generating' | 'completed' | 'failed'
  reportId: string | null
  sourceVersion: number | null
  sourceLastEventSequence: number | null
  evidenceIds: string[]
  inputHash: string | null
  artifactId: string | null
  updatedAt: string | null
}

export interface CanonicalArtifactV1 {
  artifactId: string
  kind: 'report' | 'proposal' | 'file'
  storageRef: string
  sourceVersion: number
  createdAt: string
}

export interface CanonicalConnectionStateV1 {
  connected: boolean
  connectionId: string | null
  updatedAt: string | null
}

export interface CanonicalSessionProjectionV1 {
  schemaVersion: typeof SESSION_PROJECTION_SCHEMA_VERSION
  sessionId: string
  version: number
  lastEventSequence: number
  createdAt: string
  updatedAt: string
  locale: CanonicalUiLocaleV1
  access: CanonicalSessionAccessV1
  visitor: CanonicalVisitorV1
  company: CanonicalCompanyV1
  location: CanonicalLocationV1
  modalities: CanonicalModalitiesV1
  transcript: CanonicalTranscriptEntryV1[]
  stage: FunnelStage
  userMessageCount: number
  currentIntent: ConsultationIntent | null
  conversationFlow: CanonicalConversationFlowV1
  agentState: CanonicalAgentStateV1
  researchContext: Record<string, unknown>
  evidence: CanonicalEvidenceV1[]
  sessionFacts: Record<string, CanonicalSessionFactV1>
  correctionHistory: CanonicalFactCorrectionV1[]
  turnCapabilities: Record<string, CanonicalTurnCapabilityManifestV1>
  activeTurn: CanonicalActiveTurnV1 | null
  responseOwnership: CanonicalResponseOwnershipV1 | null
  lastAgentDecision: CanonicalAgentDecisionV1 | null
  latestAgentSummary: CanonicalAgentSummaryV1 | null
  booking: CanonicalBookingStateV1
  report: CanonicalReportStateV1
  artifacts: CanonicalArtifactV1[]
  connections: {
    browser: CanonicalConnectionStateV1
    live: CanonicalConnectionStateV1
  }
  importedFromLegacy: boolean
  /** Structured workshop answers. Null until the visitor consents. */
  workshopIntake: Record<string, unknown> | null
}

export interface SessionProjectionEnvelopeV1 {
  schemaVersion: typeof SESSION_PROJECTION_SCHEMA_VERSION
  sessionId: string
  version: number
  lastEventSequence: number
  committedAt: string
  projection: CanonicalSessionProjectionV1
}

function inactiveModality(): CanonicalModalityStatusV1 {
  return { active: false, lastEventAt: null }
}

function disconnected(): CanonicalConnectionStateV1 {
  return { connected: false, connectionId: null, updatedAt: null }
}

export function createInitialSessionProjection(
  sessionId: string,
  createdAt: string,
): CanonicalSessionProjectionV1 {
  return {
    schemaVersion: SESSION_PROJECTION_SCHEMA_VERSION,
    sessionId,
    version: 0,
    lastEventSequence: 0,
    createdAt,
    updatedAt: createdAt,
    locale: 'en',
    access: {
      gated: true,
      hasCompletedConcierge: false,
      consentPersisted: false,
      permissions: {
        research: false,
        location: false,
        voice: false,
        webcam: false,
        screen: false,
        files: false,
      },
    },
    visitor: {
      name: null,
      email: null,
      role: null,
      identityConfidence: 0,
      identitySources: [],
      confirmedByUser: false,
    },
    company: {
      name: null,
      domain: null,
      website: null,
      industry: null,
      size: null,
      summary: null,
      confidence: null,
      sources: [],
    },
    location: {
      granted: false,
      latitude: null,
      longitude: null,
      city: null,
      country: null,
      updatedAt: null,
    },
    modalities: {
      text: inactiveModality(),
      voice: inactiveModality(),
      screen: inactiveModality(),
      webcam: inactiveModality(),
      file: inactiveModality(),
      tool: inactiveModality(),
    },
    transcript: [],
    stage: 'DISCOVERY',
    userMessageCount: 0,
    currentIntent: null,
    conversationFlow: createEmptyCanonicalConversationFlow(),
    agentState: createInitialCanonicalAgentState(),
    researchContext: {},
    evidence: [],
    sessionFacts: {},
    correctionHistory: [],
    turnCapabilities: {},
    activeTurn: null,
    responseOwnership: null,
    lastAgentDecision: null,
    latestAgentSummary: null,
    booking: { status: 'none', bookingId: null, updatedAt: null },
    report: {
      status: 'none',
      reportId: null,
      sourceVersion: null,
      sourceLastEventSequence: null,
      evidenceIds: [],
      inputHash: null,
      artifactId: null,
      updatedAt: null,
    },
    artifacts: [],
    connections: {
      browser: disconnected(),
      live: disconnected(),
    },
    importedFromLegacy: false,
    workshopIntake: null,
  }
}

export function envelopeSessionProjection(
  projection: CanonicalSessionProjectionV1,
  committedAt: string,
): SessionProjectionEnvelopeV1 {
  return {
    schemaVersion: SESSION_PROJECTION_SCHEMA_VERSION,
    sessionId: projection.sessionId,
    version: projection.version,
    lastEventSequence: projection.lastEventSequence,
    committedAt,
    projection,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function parseCanonicalSessionProjectionV1(
  value: unknown,
): CanonicalSessionProjectionV1 {
  if (!isRecord(value)) throw new Error('Canonical session projection must be an object')
  if (value.schemaVersion !== SESSION_PROJECTION_SCHEMA_VERSION) {
    throw new Error('Unsupported canonical session projection schema')
  }
  if (typeof value.sessionId !== 'string' || !value.sessionId) {
    throw new Error('Canonical session projection requires a sessionId')
  }
  if (!Number.isSafeInteger(value.version) || (value.version as number) < 0) {
    throw new Error('Canonical session projection has an invalid version')
  }
  if (
    !Number.isSafeInteger(value.lastEventSequence) ||
    (value.lastEventSequence as number) < 0
  ) {
    throw new Error('Canonical session projection has an invalid event sequence')
  }
  if (value.version !== value.lastEventSequence) {
    throw new Error('Canonical session projection version and sequence must match')
  }
  if (
    !Array.isArray(value.transcript) ||
    !Array.isArray(value.evidence) ||
    !isRecord(value.sessionFacts) ||
    !isRecord(value.researchContext) ||
    !isRecord(value.access) ||
    !isRecord(value.modalities)
  ) {
    throw new Error('Canonical session projection is missing required collections')
  }
  const projection = value as unknown as CanonicalSessionProjectionV1
  const conversationFlow = normalizeCanonicalConversationFlow(
    value.conversationFlow,
    typeof value.userMessageCount === 'number' ? value.userMessageCount : 0,
  )
  const rawAgentState = isRecord(value.agentState)
    ? (value.agentState as Partial<CanonicalAgentStateV1>)
    : {}
  return {
    ...projection,
    conversationFlow:
      conversationFlow ?? createEmptyCanonicalConversationFlow(projection.userMessageCount),
    agentState: mergeCanonicalAgentState(createInitialCanonicalAgentState(), rawAgentState),
    latestAgentSummary: normalizeCanonicalAgentSummary(value.latestAgentSummary),
    workshopIntake:
      value.workshopIntake && typeof value.workshopIntake === 'object'
        ? (value.workshopIntake as Record<string, unknown>)
        : null,
  }
}

export function parseSessionProjectionEnvelopeV1(
  value: unknown,
): SessionProjectionEnvelopeV1 {
  if (!isRecord(value)) throw new Error('Session projection envelope must be an object')
  if (value.schemaVersion !== SESSION_PROJECTION_SCHEMA_VERSION) {
    throw new Error('Unsupported session projection envelope schema')
  }
  if (typeof value.sessionId !== 'string' || !value.sessionId) {
    throw new Error('Session projection envelope requires a sessionId')
  }
  if (
    !Number.isSafeInteger(value.version) ||
    !Number.isSafeInteger(value.lastEventSequence) ||
    value.version !== value.lastEventSequence
  ) {
    throw new Error('Session projection envelope has invalid version metadata')
  }
  if (
    typeof value.committedAt !== 'string' ||
    Number.isNaN(Date.parse(value.committedAt))
  ) {
    throw new Error('Session projection envelope requires a committedAt timestamp')
  }
  const projection = parseCanonicalSessionProjectionV1(value.projection)
  if (
    projection.sessionId !== value.sessionId ||
    projection.version !== value.version ||
    projection.lastEventSequence !== value.lastEventSequence
  ) {
    throw new Error('Session projection envelope does not match its projection')
  }
  return {
    schemaVersion: SESSION_PROJECTION_SCHEMA_VERSION,
    sessionId: value.sessionId,
    version: value.version,
    lastEventSequence: value.lastEventSequence,
    committedAt: value.committedAt,
    projection,
  }
}
